<template>
  <div class="requirement-group">
    <div v-if="first" class="pt-2 pl-3">
      <v-chip :color="themes.candidate.light.muted" dark>
        <v-avatar>
          <v-btn color="muted" fab>
            <v-icon> {{ header.icon }} </v-icon>
          </v-btn>
        </v-avatar>
        <span class="subheading nowrap pl-2">{{ header.text }}</span>
      </v-chip>
    </div>

    <v-container class="py-0 ml-4">
      <v-layout row wrap align-start>
        <Requirement
          v-if="group.name"
          :icon="header.icon"
          :satisfied="group.satisfied"
          class="py-3 pr-2"
        >
          <span></span>
        </Requirement>
        <span
          v-if="group.name"
          class="py-3 descriptor"
          :class="themes.candidate.light.mutedText"
        >
          {{ group.name }}
        </span>
        <div
          v-else
          v-for="requirement in requirements"
          :key="`requirement-${requirement.id}`"
          :is="requirement.type.split('::')[1]"
          :requirement="requirement"
          :groupSatisfied="group.satisfied"
          :first="requirement == requirements[0]"
          class="py-3 pr-2"
        />
      </v-layout>
    </v-container>
    <div
      v-if="!last"
      class="text-xs-left py-2 ml-5"
      :class="themes.candidate.light.mutedText"
    >
      {{ group.mandatory ? "AND" : "OR" }}
    </div>
  </div>
</template>

<script>
// Components
import Requirement from "@/components/guest/opportunities/requirements/Base";

import Age from "@/components/guest/opportunities/requirements/Age";
import Nationality from "@/components/guest/opportunities/requirements/Nationality";
import Gender from "@/components/guest/opportunities/requirements/Gender";
import Location from "@/components/guest/opportunities/requirements/Location";

import ExperienceDiscipline from "@/components/guest/opportunities/requirements/ExperienceDiscipline";
import ExperienceDuration from "@/components/guest/opportunities/requirements/ExperienceDuration";
import ExperienceIndustry from "@/components/guest/opportunities/requirements/ExperienceIndustry";
import ExperienceType from "@/components/guest/opportunities/requirements/ExperienceType";
import QualificationCategory from "@/components/guest/opportunities/requirements/QualificationCategory";
import QualificationCommencement from "@/components/guest/opportunities/requirements/QualificationCommencement";
import QualificationCompletion from "@/components/guest/opportunities/requirements/QualificationCompletion";
import QualificationDiscipline from "@/components/guest/opportunities/requirements/QualificationDiscipline";
import QualificationGrade from "@/components/guest/opportunities/requirements/QualificationGrade";
import QualificationInstitution from "@/components/guest/opportunities/requirements/QualificationInstitution";
import QualificationLevel from "@/components/guest/opportunities/requirements/QualificationLevel";
import QualificationType from "@/components/guest/opportunities/requirements/QualificationType";
import StudyRight from "@/components/guest/opportunities/requirements/StudyRight";
import WorkRight from "@/components/guest/opportunities/requirements/WorkRight";
import WorkExperience from "@/components/guest/opportunities/requirements/WorkExperience";

// Vue Component
export default {
  components: {
    Requirement,
    Age,
    Gender,
    Location,
    Nationality,
    ExperienceDiscipline,
    ExperienceDuration,
    ExperienceIndustry,
    ExperienceType,
    QualificationCategory,
    QualificationCommencement,
    QualificationCompletion,
    QualificationDiscipline,
    QualificationGrade,
    QualificationInstitution,
    QualificationLevel,
    QualificationType,
    StudyRight,
    WorkRight,
    WorkExperience,
  },
  props: {
    group: { type: Object, required: true },
    first: { type: Boolean, default: () => false },
    last: { type: Boolean, default: () => false },
  },
  computed: {
    header() {
      if (this.group.type == "ProfileRequirementGroup") {
        return { text: "Profile requirements", icon: "mdi-pencil" };
      }
      if (this.group.type == "ProfileQualificationRequirementGroup") {
        return {
          text: "Qualification or study programme requirements",
          icon: "mdi-school",
        };
      }
      if (this.group.type == "ExperienceRequirementGroup") {
        return { text: "Experience requirements", icon: "mdi-briefcase" };
      }
      return null;
    },
    profileQualification() {
      if (this.group.profileQualification) {
        return this.group.profileQualification;
      } else if (this.group.profileQualifications) {
        return this.group.profileQualifications
          .slice()
          .sort((first, second) => {
            const firstSatisfies = first.requirements.filter(
              (r) => r.satisfied
            ).length;
            const secondSatisfies = second.requirements.filter(
              (r) => r.satisfied
            ).length;
            // Negative means first is better, positive second is better
            return secondSatisfies - firstSatisfies;
          })[0];
      } else {
        return null;
      }
    },
    experience() {
      if (this.group.experiences) {
        if (this.group.experiences.length > 1) {
          return this.group.experiences.slice().sort((first, second) => {
            const firstSatisfies = first.requirements.filter(
              (r) => r.satisfied
            ).length;
            const secondSatisfies = second.requirements.filter(
              (r) => r.satisfied
            ).length;
            // Negative means first is better, positive second is better
            return secondSatisfies - firstSatisfies;
          })[0];
        } else {
          return this.group.experiences[0];
        }
      } else {
        return null;
      }
    },
    requirements() {
      if (this.profileQualification) {
        return this.profileQualification.requirements;
      } else if (this.experience) {
        return this.experience.requirements;
      } else {
        return this.group.requirements;
      }
    },
  },
};
</script>

<style lang="scss">
.requirement-group {
  .v-chip {
    max-width: 100%;

    .v-chip__content {
      max-width: 100%;

      .nowrap {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .descriptor {
    align-self: center;
  }
}
</style>
